import { useRef, useState, useEffect, useCallback } from "react"
import { createPortal } from "react-dom"
import { X, Camera, ImagePlus, Loader2, Trash2 } from "lucide-react"
import { uploadReportPhoto } from "../lib/supabase"
import { useModalOpen } from "../lib/useModalOpen"
import { GlitchWord } from "./GlitchWord"

export interface MediaCaptureModalProps {
  open: boolean
  onClose: () => void
  onPhotosAdded: (urls: string[]) => void
  uploadPrefix?: string
}

export function MediaCaptureModal({
  open,
  onClose,
  onPhotosAdded,
  uploadPrefix = "draft",
}: MediaCaptureModalProps) {
  useModalOpen(open)
  const galleryInputRef = useRef<HTMLInputElement>(null)
  const videoRef = useRef<HTMLVideoElement>(null)
  const streamRef = useRef<MediaStream | null>(null)

  const [captured, setCaptured] = useState<string[]>([])
  const [uploading, setUploading] = useState(false)
  const [cameraReady, setCameraReady] = useState(false)
  const [cameraError, setCameraError] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [flash, setFlash] = useState(0)

  useEffect(() => {
    if (!open) {
      stopCamera()
      setCaptured([])
      setCameraReady(false)
      setCameraError(null)
      setError(null)
      return
    }
    startCamera()
  }, [open])

  function startCamera() {
    setCameraError(null)
    navigator.mediaDevices
      .getUserMedia({ video: { facingMode: "environment" } })
      .catch(() => navigator.mediaDevices.getUserMedia({ video: true }))
      .then((s) => {
        streamRef.current = s
        if (videoRef.current) videoRef.current.srcObject = s
        setCameraReady(true)
      })
      .catch((e) => {
        setCameraError(e?.message || "Camera not available")
        setCameraReady(false)
      })
  }

  function stopCamera() {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop())
      streamRef.current = null
    }
  }

  const capturePhoto = useCallback(async () => {
    const video = videoRef.current
    const stream = streamRef.current
    if (!video || !stream || video.readyState < 2 || uploading) return
    setError(null)
    setUploading(true)
    setFlash((p) => p + 1)
    try {
      const canvas = document.createElement("canvas")
      canvas.width = video.videoWidth
      canvas.height = video.videoHeight
      const ctx = canvas.getContext("2d")
      if (!ctx) throw new Error("Canvas not available")
      ctx.translate(canvas.width, 0)
      ctx.scale(-1, 1)
      ctx.drawImage(video, 0, 0)
      const blob = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, "image/jpeg", 0.92)
      )
      if (!blob) throw new Error("Failed to create image")
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" })
      const url = await uploadReportPhoto(file, { prefix: uploadPrefix })
      setCaptured((prev) => [...prev, url])
    } catch (e) {
      setError(e instanceof Error ? e.message : "Capture failed")
    } finally {
      setUploading(false)
    }
  }, [uploading, uploadPrefix])

  async function handleGalleryFiles(files: FileList | null) {
    if (!files?.length) return
    setError(null)
    setUploading(true)
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i]
        if (!file.type.startsWith("image/")) continue
        const url = await uploadReportPhoto(file, { prefix: uploadPrefix })
        setCaptured((prev) => [...prev, url])
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Upload failed")
    } finally {
      setUploading(false)
      if (galleryInputRef.current) galleryInputRef.current.value = ""
    }
  }

  function handleDone() {
    if (captured.length) onPhotosAdded(captured)
    onClose()
  }

  function removePhoto(idx: number) {
    setCaptured((prev) => prev.filter((_, i) => i !== idx))
  }

  if (!open) return null

  return createPortal(
    <>
      <input
        ref={galleryInputRef}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => handleGalleryFiles(e.target.files)}
      />
      <div className="fixed inset-0 z-[200] flex flex-col glass-backdrop">
        <div className="flex-1 flex flex-col max-w-lg w-full mx-auto">
          {/* Terminal header */}
          <div className="shrink-0 flex items-center justify-between px-4 py-3">
            <div className="flex flex-col gap-0.5">
              <span className="text-[11px] font-mono font-bold uppercase tracking-widest text-white/90">
                Capture
              </span>
              <div className="font-mono text-[9px] flex items-center gap-1.5">
                <span className="text-white/15">0x02</span>
                <span className="text-white/10">&middot;</span>
                <span className="text-teal-400/60">
                  <GlitchWord word="media" intensity={1} colorBase="teal" />.stream()
                </span>
              </div>
            </div>
            <button
              type="button"
              onClick={handleDone}
              className="p-1.5 rounded-lg text-white/30 hover:text-white/70 hover:bg-white/[0.06] transition-colors"
              aria-label="Close"
            >
              <X className="w-4 h-4" />
            </button>
          </div>

          {/* Viewfinder */}
          <div className="flex-1 min-h-0 px-3 pb-2">
            {cameraError ? (
              <div className="h-full flex flex-col items-center justify-center gap-4 rounded-xl border border-white/[0.06] bg-black/30">
                <p className="text-[11px] font-mono text-white/40 text-center px-6">{cameraError}</p>
                <button
                  type="button"
                  onClick={() => galleryInputRef.current?.click()}
                  className="flex items-center gap-2 px-5 py-3 rounded-xl border border-teal-400/25 bg-teal-500/15 text-teal-400 font-mono text-[11px] font-bold uppercase tracking-wider hover:bg-teal-500/25 transition-colors"
                >
                  <ImagePlus className="w-4 h-4" />
                  Choose from gallery
                </button>
              </div>
            ) : (
              <div className="relative h-full rounded-xl overflow-hidden bg-black border border-white/[0.06]">
                <video
                  ref={videoRef}
                  autoPlay
                  playsInline
                  muted
                  className="w-full h-full object-cover scale-x-[-1]"
                />
                {/* Corner reticles */}
                <div className="absolute top-3 left-3 w-4 h-4 border-t border-l border-white/20 rounded-tl" />
                <div className="absolute top-3 right-3 w-4 h-4 border-t border-r border-white/20 rounded-tr" />
                <div className="absolute bottom-3 left-3 w-4 h-4 border-b border-l border-white/20 rounded-bl" />
                <div className="absolute bottom-3 right-3 w-4 h-4 border-b border-r border-white/20 rounded-br" />
                {/* Capture flash */}
                {flash > 0 && (
                  <div
                    key={flash}
                    className="absolute inset-0 bg-white/30 animate-[fadeOut_200ms_ease-out_forwards]"
                  />
                )}
              </div>
            )}
          </div>

          {/* Thumbnail strip */}
          {captured.length > 0 && (
            <div className="shrink-0 px-3 pb-2">
              <div className="flex items-center gap-1.5 overflow-x-auto py-1 scrollbar-none">
                {captured.map((url, i) => (
                  <div key={url} className="relative shrink-0 w-12 h-12 rounded-lg overflow-hidden border border-white/[0.08] group">
                    <img src={url} alt="" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => removePhoto(i)}
                      className="absolute inset-0 flex items-center justify-center bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity"
                    >
                      <Trash2 className="w-3 h-3 text-red-400" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Action bar */}
          <div className="shrink-0 px-3 pb-4 pt-1">
            {error && (
              <div className="text-[10px] font-mono text-red-400/80 mb-2">{error}</div>
            )}
            <div className="flex items-center justify-between gap-3">
              {/* Gallery */}
              <button
                type="button"
                onClick={() => galleryInputRef.current?.click()}
                className="p-3 rounded-xl border border-white/[0.08] bg-white/[0.04] text-white/40 hover:bg-white/[0.08] hover:text-white/60 transition-colors"
                aria-label="Gallery"
              >
                <ImagePlus className="w-5 h-5" />
              </button>

              {/* Capture button */}
              {!cameraError && (
                <button
                  type="button"
                  disabled={uploading || !cameraReady}
                  onClick={capturePhoto}
                  className="relative w-16 h-16 rounded-full border-[3px] border-white/30 flex items-center justify-center disabled:opacity-40 transition-opacity group"
                  aria-label="Capture"
                >
                  {uploading ? (
                    <Loader2 className="w-6 h-6 text-white animate-spin" />
                  ) : (
                    <div className="w-12 h-12 rounded-full bg-white group-hover:bg-white/90 group-active:scale-90 transition-transform" />
                  )}
                </button>
              )}

              {/* Done */}
              <button
                type="button"
                onClick={handleDone}
                className="flex items-center gap-1.5 px-4 py-3 rounded-xl border border-teal-400/25 bg-teal-500/15 text-teal-400 font-mono text-[11px] font-bold uppercase tracking-wider hover:bg-teal-500/25 transition-colors"
              >
                Done
                {captured.length > 0 && (
                  <span className="tabular-nums">{captured.length}</span>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </>,
    document.body
  )
}
